import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { strings } from '/imports/strings';
import { Button } from '/imports/ui/Button';
import { DevToolSection } from '/imports/ui/DevToolSection';
import { toastOnError } from '/imports/ui/utils';
import { copyToClipboard, formatDateTime } from '/imports/utils';
import { quickbooksAuthUrl, quickbooksDisconnect, quickbooksRefreshNow, quickbooksStatus, QuickbooksStatus } from '/imports/api/quickbooks';

/** Dev tool: connect the org's QuickBooks company (OAuth2, read-only) and show what is attached. */
export const DevToolQuickbooks = () => {
  const [status, setStatus] = useState<QuickbooksStatus>();
  const load = toastOnError(async () => setStatus(await quickbooksStatus.call(undefined)));
  useEffect(() => { load(); }, []);
  // Intuit redirects back to /project-management?quickbooks=… — surface the outcome wherever the admin
  // lands back on this page.
  useEffect(() => {
    const result = new URLSearchParams(window.location.search).get('quickbooks');
    if (result === 'connected') toast.success(strings.Done());
    else if (result === 'failed') toast.error(strings.Error());
  }, []);
  const connect = toastOnError(async () => {
    // Full-page navigation, not a popup: Intuit's consent screen refuses to render in a frame.
    window.location.href = await quickbooksAuthUrl.call({});
  });
  // A link to hand to whoever actually holds the QuickBooks login — an accountant, typically, who has
  // no Luminia account. It carries the same one-time nonce, minted with the long TTL so it survives
  // the trip through an inbox, and completing it stores the connection against THIS org.
  const copyLink = toastOnError(async () => {
    await copyToClipboard(await quickbooksAuthUrl.call({ shareable: true }));
    toast.success(strings.CopiedToClipboard());
  });
  // Intuit expires the refresh token on an IDLE clock, so exercising it IS the maintenance. The weekly
  // cron does this unattended; the button is for confirming a connection is alive without waiting a week.
  const refresh = toastOnError(async () => {
    const result = await quickbooksRefreshNow.call(undefined);
    await load();
    if (result.ok) toast.success(`${strings.QuickbooksTokenRefreshed()} ${formatDateTime(result.accessExpiresAt)}`);
    else toast.error(result.message || strings.Error());
  });
  const disconnect = toastOnError(async () => {
    await quickbooksDisconnect.call(undefined);
    await load();
    toast.success(strings.Done());
  });
  return (
    <DevToolSection
      title="QuickBooks"
      description="Connect the org's QuickBooks company for the (pull-only) cash reconciliation. <b>Read-only</b> — Luminia never writes back."
    >
      {
        status?.connected ?
          <div>
            <div className="meta-row">
              {status.companyName || status.realmId} · {status.environment}
              {status.connectedByName ? ` · ${status.connectedByName}` : ''}
            </div>
            {status.expired && <div className="meta-row">{strings.QuickbooksConnectionExpired()}</div>}
            {status.needsReconnect && !status.expired && <div className="meta-row">{strings.QuickbooksAccessRevoked()}</div>}
            <div className="meta-row">
              {strings.QuickbooksLastRefreshed()}: {status.refreshedAt ? formatDateTime(status.refreshedAt) : strings.QuickbooksNeverRefreshed()}
              {status.refreshExpiresAt ? ` · ${strings.QuickbooksRefreshTokenExpires()} ${formatDateTime(status.refreshExpiresAt)}` : ''}
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
              <Button className="secondary" onClick={() => refresh()}>{strings.RefreshToken()}</Button>
              <Button className="secondary" onClick={() => connect()}>{strings.Reconnect()}</Button>
              <Button className="secondary" onClick={() => copyLink()}>{strings.CopyLink()}</Button>
              <Button className="secondary" onClick={() => disconnect()}>{strings.Disconnect()}</Button>
            </div>
          </div>
          :
          <div>
            <div className="meta-row">{strings.NotConnected()}{status?.environment ? ` · ${status.environment}` : ''}</div>
            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
              <Button onClick={() => connect()}>{strings.ConnectQuickbooks()}</Button>
              <Button className="secondary" onClick={() => copyLink()}>{strings.CopyLink()}</Button>
            </div>
            <div className="meta-row">{strings.QuickbooksShareTip()}</div>
          </div>
      }
    </DevToolSection>
  );
};
